/**
 * Multi-tenant client guard.
 *
 * Security model (see `src/docs/Guardrails.md`):
 * The tenant id sent in `X-Tenant-Id` is a **selector, never an authorization
 * proof**. The BFF re-derives the tenant from the authenticated principal and
 * rejects requests whose header does not match that principal's memberships.
 * This module exists so the client has exactly one place where tenant identity
 * is read, validated, and turned into headers — no component may invent one.
 */

export const TENANT_HEADER = 'X-Tenant-Id'
export const TENANT_STORAGE_KEY = 'oc_tenant_id'
export const TOKEN_STORAGE_KEY = 'oc_access_token'
export const REFRESH_TOKEN_STORAGE_KEY = 'oc_refresh_token'

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const MAX_TENANT_ID_LENGTH = 64

export class TenantContextError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TenantContextError'
  }
}

/** True when the value is a well-formed UUID (the documented public id format). */
export function isUuidTenantId(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value)
}

/** A tenant id must be a short, non-empty, whitespace-free opaque string. */
export function isTenantId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= MAX_TENANT_ID_LENGTH &&
    !/\s/.test(value)
  )
}

/** Throws when a tenant id is missing or malformed. Use at every tenant boundary. */
export function assertTenantId(value: unknown, context = 'request'): string {
  if (!isTenantId(value)) {
    throw new TenantContextError(
      `Missing or malformed tenant id for ${context}. ` +
        'Tenant-scoped operations must never run without a validated tenant.',
    )
  }
  return value
}

/** Read the active tenant id from storage, returning null when absent/invalid. */
export function readStoredTenantId(
  storage: Pick<Storage, 'getItem'> = localStorage,
): string | null {
  const raw = storage.getItem(TENANT_STORAGE_KEY)
  return isTenantId(raw) ? raw : null
}

/** Build the tenant headers for an outbound request. Empty when unauthenticated. */
export function buildTenantHeaders(
  tenantId?: string | null,
): Record<string, string> {
  return isTenantId(tenantId) ? { [TENANT_HEADER]: tenantId } : {}
}

/**
 * Namespace a react-query cache key by tenant.
 *
 * OWASP: a shared cache key for data that varies by tenant leaks across tenant
 * boundaries. Every tenant-scoped query must go through this helper.
 */
export function tenantScopedQueryKey(
  tenantId: string | null | undefined,
  ...parts: unknown[]
): unknown[] {
  return ['tenant', tenantId ?? 'anonymous', ...parts]
}
